/**
 * 編集した SSML を合成へ送る前の最小整形と検証 (純関数 / 計画 0.5 の
 * 「SSML 送信前の最小整形 (`<speak>` 補完)」)。
 *
 * ## なぜ純関数に切り出すか
 *
 * 「どこまで画面が直すか」は解釈違いが出やすい箇所なので、UI と分けて
 * テストで固定する。UI 側は「押せるか / エラーを出すか」だけを担う。
 *
 * ## ⚠️ ここで構文チェックはしない
 *
 * SSML の構文エラーは **Polly が `InvalidSsmlException` → 400** に変換する
 * 経路が既にある (計画 0.6 / 8.3.4)。画面側でタグの整合性を検査すると
 * 二重実装になり、「画面は通すが Polly は落ちる」「その逆」という
 * 説明のつかない差異が生まれる。ここでは **送っても無意味な入力**
 * (空 / 長すぎる) だけを弾き、残りはサーバの判断に委ねる。
 */
import { UNSUPPORTED_SSML_TAGS } from '../../data/ssmlUnsupportedTags';
import type { UnsupportedSsmlTag } from '../../data/ssmlUnsupportedTags';

/**
 * 編集 SSML の長さ上限 (文字)。
 *
 * サーバ側 `lambda/control-panel-api/index.py` の `MAX_PREVIEW_TEXT_LENGTH`
 * と **同じ値**。画面側で先に弾くのは、送信して 400 が返るより
 * 「押す前に気づける」方が親切だからで、サーバ側の検証を省く意味ではない。
 *
 * ⚠️ サーバ側を変えたらここも変えること。
 */
export const MAX_EDITED_SSML_LENGTH = 3000;

/** {@link prepareEditedSsml} の結果 (成功なら `ssml`、失敗なら `error`)。 */
export type PrepareEditedSsmlResult = { ok: true; ssml: string } | { ok: false; error: string };

/**
 * 編集された SSML を `/audio-preview` へ送れる形に整える。
 *
 * 行うのは以下の 3 点だけ。
 *
 *   1. 前後の空白を落とす (末尾改行だけの差で再合成が走るのを防ぐ)
 *   2. 空なら弾く (送っても 400 になるだけで、利用者に情報が返らない)
 *   3. `<speak>` で囲まれていなければ囲む (編集時に消しがちな要素の補完)
 *
 * @param raw テキストエリアの生の値
 * @returns 送信できる SSML、または利用者に見せるエラー文
 */
export function prepareEditedSsml(raw: string): PrepareEditedSsmlResult {
  const trimmed = raw.trim();
  if (trimmed === '') {
    // 空のまま再作成させると Polly の 400 だけが返り、原因が伝わらない。
    return { ok: false, error: 'SSML が空です。内容を入力してください。' };
  }
  /*
   * ⚠️ 長さは **補完後** で判定する。`<speak>` を足した結果 Polly の上限を
   * 超えると、画面では通ったのに 400 という分かりにくい状態になる。
   */
  const wrapped = wrapWithSpeak(trimmed);
  if (wrapped.length > MAX_EDITED_SSML_LENGTH) {
    return {
      ok: false,
      error: `SSML が長すぎます (${MAX_EDITED_SSML_LENGTH} 文字以内にしてください)。`,
    };
  }
  return { ok: true, ssml: wrapped };
}

/**
 * `<speak>` で囲まれていない SSML を囲む。
 *
 * 編集中に開始タグ / 終了タグを消してしまうのはよくある操作ミスで、
 * そのまま送ると Polly の 400 になる。補完できるものは補完して
 * 「本題 (聞こえ方の調整) 以外でつまずかせない」。
 *
 * ⚠️ 属性付き (`<speak version="1.1">`) も既に囲まれているとみなす。
 * 属性を落として単純な `<speak>` に置き換えると、利用者が意図して
 * 書いた指定を画面が勝手に消すことになる。
 */
function wrapWithSpeak(ssml: string): string {
  const hasOpen = /^<speak(\s[^>]*)?>/.test(ssml);
  const hasClose = ssml.endsWith('</speak>');
  if (hasOpen && hasClose) {
    return ssml;
  }
  /*
   * 片方だけある場合 (開始タグはあるが終了タグが無い等) は **囲まない**。
   * 機械的に足すと `<speak><speak>…</speak>` のような二重入れ子になり、
   * かえって原因の分かりにくい 400 を生む。サーバの構文エラーに委ねて
   * 「タグの対応が取れていない」ことを利用者に気づかせる方が安全。
   */
  if (hasOpen || hasClose) {
    return ssml;
  }
  return `<speak>${ssml}</speak>`;
}

/**
 * pitch 属性の値 (引用符 `"` / `'` / バックスラッシュ付き / 曲がった引用符 / 引用符無し)。
 * サーバ側 `lambda/common/ssml_tuning.py` の `_PITCH_VALUE_PATTERN` と揃える。
 */
const PITCH_ATTR_RE =
  /\s*\bpitch\s*=\s*(?:\\?"[^"]*?\\?"|\\?'[^']*?\\?'|[“”][^“”]*[“”]|[‘’][^‘’]*[‘’]|[^\s/>]+)/gi;
/** pitch 属性の存在判定 (値の形式は問わない)。 */
const PITCH_PRESENCE_RE = /\bpitch\s*=/i;
/** `<prosody ...>` 開始タグ / `</prosody>` 終了タグ。 */
const PROSODY_TOKEN_RE = /<prosody\b[^>]*>|<\/prosody\s*>/gi;
/** 属性が 1 つも残っていない prosody 開始タグ。 */
const EMPTY_PROSODY_RE = /^<prosody\s*\/?>$/i;
/** 任意のタグ。 */
const ANY_TAG_RE = /<[^<>]*>/g;

/**
 * SSML から pitch 属性を取り除く (Takumi + Polly は pitch を値に関係なく拒否する)。
 *
 * ## なぜ画面側にも持つか (多層防御)
 *
 * pitch の除去はサーバ (`POST /ssml-tuning/samples` の正規化) が正源だが、
 * 「デプロイ前の古い Lambda が応答した」「新しい書き方の揺れがサーバをすり抜けた」
 * 場合でも、**候補の表示と自動合成に pitch を渡さない** ための最後の砦として置く。
 * 処理はサーバ実装と同じ規則 (pitch だけの prosody はタグだけ外し本文は残す)。
 *
 * ⚠️ 対象は **タグ内** の属性のみ。本文中の「pitch=」という文字列は変えない。
 */
export function stripPitchAttributes(ssml: string): string {
  if (!PITCH_PRESENCE_RE.test(ssml)) {
    return ssml;
  }
  // ① prosody: 開始 / 終了タグを対応付け、pitch だけだったタグは組で外す。
  const droppedStack: boolean[] = [];
  const withoutProsodyPitch = ssml.replace(PROSODY_TOKEN_RE, (token) => {
    if (token.startsWith('</')) {
      const dropped = droppedStack.pop() ?? false;
      return dropped ? '' : token;
    }
    const stripped = token.replace(PITCH_ATTR_RE, '');
    const drop = stripped !== token && EMPTY_PROSODY_RE.test(stripped);
    if (!/\/\s*>$/.test(token)) {
      droppedStack.push(drop);
    }
    return drop ? '' : stripped;
  });
  // ② それ以外のタグ (`<s pitch=...>` 等) に残った pitch も消す。
  return withoutProsodyPitch.replace(ANY_TAG_RE, (tag) =>
    PITCH_PRESENCE_RE.test(tag) ? tag.replace(PITCH_ATTR_RE, '') : tag,
  );
}

/** SSML のタグ内に pitch 属性が残っているか。 */
export function containsPitchAttribute(ssml: string): boolean {
  return (ssml.match(ANY_TAG_RE) ?? []).some((tag) => PITCH_PRESENCE_RE.test(tag));
}

/** 正規表現の特殊文字をエスケープする (タグ名に `:` / `-` を含むため)。 */
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** `<name ...>中身</name>` (自己終了タグは含まない) を拾う正規表現。 */
function pairedTagRe(name: string): RegExp {
  const escaped = escapeRegExp(name);
  return new RegExp(`<${escaped}(?:\\s[^>]*)?(?<!/)>([\\s\\S]*?)</${escaped}\\s*>`, 'gi');
}

function namesPattern(tags: readonly UnsupportedSsmlTag[]): string {
  return tags.map((tag) => escapeRegExp(tag.name)).join('|');
}

/** `drop` のタグ (中身ごと捨てる)。 */
const DROP_RES = UNSUPPORTED_SSML_TAGS.filter((t) => t.removal === 'drop').map((t) =>
  pairedTagRe(t.name),
);
/** `reading` のタグ (中身を括弧書きへ)。 */
const READING_RES = UNSUPPORTED_SSML_TAGS.filter((t) => t.removal === 'reading').map((t) =>
  pairedTagRe(t.name),
);
/**
 * 全利用不可タグの開始 / 終了 / 自己終了タグ (最後にタグだけ外す)。
 * ⚠️ 名前の直後を `[\s/>]` に限定し、`<rate>` / `<rtx>` のような別名タグを消さない。
 */
const UNSUPPORTED_TAG_SOURCE = `</?(?:${namesPattern(UNSUPPORTED_SSML_TAGS)})(?=[\\s/>])[^>]*>`;
const UNSUPPORTED_TAG_RE = new RegExp(UNSUPPORTED_TAG_SOURCE, 'gi');
const UNSUPPORTED_TAG_PRESENCE_RE = new RegExp(UNSUPPORTED_TAG_SOURCE, 'i');

/**
 * SSML から Amazon Polly (Takumi) で利用できないタグを取り除く (本文は残す)。
 *
 * - `<emphasis>梅田</emphasis>` → `梅田`
 * - `<ruby>武蔵小杉<rt>むさしこすぎ</rt></ruby>方面` → `武蔵小杉（むさしこすぎ）方面`
 *   (放送文の元の表記へ戻す)
 *
 * 対象タグは `data/ssmlUnsupportedTags.ts` (正源 `lambda/common/ssml_unsupported_tags.py`
 * からの生成物) の一覧で、規則はサーバ側 `strip_unsupported_tags` と同じ。
 * サーバが正源で、ここは古い Lambda / すり抜けに備えた最後の砦。
 */
export function stripUnsupportedTags(ssml: string): string {
  if (!UNSUPPORTED_TAG_PRESENCE_RE.test(ssml)) {
    return ssml;
  }
  let result = ssml;
  for (const re of DROP_RES) {
    result = result.replace(re, '');
  }
  for (const re of READING_RES) {
    result = result.replace(re, (_m, reading: string) => `（${reading.trim()}）`);
  }
  return result.replace(UNSUPPORTED_TAG_RE, '');
}

/** SSML に利用不可タグが残っているか。 */
export function containsUnsupportedTag(ssml: string): boolean {
  return UNSUPPORTED_TAG_PRESENCE_RE.test(ssml);
}

/**
 * Amazon Polly (Takumi) が受け付けない SSML 要素 (pitch 属性 / 利用不可タグ) を取り除く。
 */
export function sanitizeUnsupportedSsml(ssml: string): string {
  return stripUnsupportedTags(stripPitchAttributes(ssml));
}

/**
 * プレーンテキスト (Step1 の放送テキスト) を `/audio-preview` へ送れる SSML にする。
 *
 * ⚠️ `& < >` をエスケープしてから `<speak>` で囲む。
 * 放送テキストは **SSML タグを含まないプレーンテキスト** (Step1 の約束) なので、
 * 記号をそのまま送ると Polly が SSML として解釈して 400 になる。
 * 長さの上限チェックは `prepareEditedSsml` (Step3 の再作成と同じ規則) に任せる。
 */
export function buildPreviewSsml(text: string): PrepareEditedSsmlResult {
  const escaped = text.trim().replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  if (escaped === '') {
    return { ok: false, error: '放送テキストが空です。内容を入力してから試聴してください。' };
  }
  return prepareEditedSsml(`<speak>${escaped}</speak>`);
}
